/**
 * Lot Polygon Colors DOM Component
 *
 * Sole owner of color-related class mutations on the ISM SVG overlay.
 * Applies base color classes, selected state, hover state, and chip-filter matched/unmatched
 * state to lot <path> elements. Does not know about Redux or business logic — receives the lot
 * status map (and, for filtering, the matched lot id set) from the plugin.
 *
 * @module nativeInteractiveSiteMap/components/lotPolygonColors.dom
 */

import LotStatus from '../enums/lotStatus';

const LOT_CLASS_BASE = 'nhs-c-native-interactive-site-map__lot';
const LOT_SELECTED_CLASS = `${LOT_CLASS_BASE}--selected`;
const LOT_HOVERED_CLASS = `${LOT_CLASS_BASE}--hovered`;
const LOT_NON_INTERACTIVE_CLASS = `${LOT_CLASS_BASE}--non-interactive`;
const LOT_UNMATCHED_CLASS = `${LOT_CLASS_BASE}--unmatched`;

/**
 * @param {SVGSVGElement} svgElement
 * @param {string} lotId
 * @returns {Element|null}
 */
function findLotElement(svgElement, lotId) {
  return svgElement.getElementById(lotId);
}

/**
 * Creates a controller for color-related CSS class mutations on lot <path> elements
 * within the given SVG element.
 *
 * @param {SVGSVGElement} svgElement - The root SVG element containing lot <path> elements
 * @returns {{ applyFilter: Function, setSelected: Function,
 *   setHovered: Function, clearHovered: Function }}
 */
function createLotPolygonColors(svgElement) {
  const state = {
    currentHoveredLotId: null
  };

  return {
    /**
     * Applies the status class (and non-interactive flag for NotFound lots) to every lot
     * element, and toggles the unmatched (grey) class based on whether its id is present in
     * matchedLotIds. Status never changes after the site map loads, so re-adding it here on
     * every call is a harmless no-op (classList.add is idempotent) — the same function covers
     * both the initial paint and every later chip change, instead of keeping a separate
     * one-time "initial colors" entry point in sync with this one.
     *
     * @param {Record<string, string>} lotStatusMap - { lotId → status string }
     * @param {Set<string>} matchedLotIds - lot ids that match the currently active chip
     */
    applyFilter(lotStatusMap, matchedLotIds) {
      Object.entries(lotStatusMap).forEach(([lotId, status]) => {
        const element = findLotElement(svgElement, lotId);
        if (!element) {
          return;
        }
        element.classList.add(`${LOT_CLASS_BASE}--${status}`);
        if (status === `${LotStatus.NotFound}`) {
          element.classList.add(LOT_NON_INTERACTIVE_CLASS);
        }
        element.classList.toggle(LOT_UNMATCHED_CLASS, !matchedLotIds.has(lotId));
      });
    },

    /**
     * Moves the selected class from prevLotId to newLotId.
     * Passing null for either argument is safe.
     *
     * @param {string|null} prevLotId
     * @param {string|null} newLotId
     */
    setSelected(prevLotId, newLotId) {
      if (prevLotId) {
        findLotElement(svgElement, prevLotId)?.classList.remove(LOT_SELECTED_CLASS);
      }
      if (newLotId) {
        findLotElement(svgElement, newLotId)?.classList.add(LOT_SELECTED_CLASS);
      }
    },

    /**
     * Adds the hovered class to the given lot element.
     * Automatically clears the previously hovered element first.
     *
     * @param {string} lotId
     */
    setHovered(lotId) {
      if (state.currentHoveredLotId && state.currentHoveredLotId !== lotId) {
        findLotElement(svgElement, state.currentHoveredLotId)?.classList.remove(LOT_HOVERED_CLASS);
      }
      findLotElement(svgElement, lotId)?.classList.add(LOT_HOVERED_CLASS);
      state.currentHoveredLotId = lotId;
    },

    /**
     * Removes the hovered class from whichever element is currently hovered.
     * No-op if nothing is currently hovered.
     */
    clearHovered() {
      if (state.currentHoveredLotId) {
        findLotElement(svgElement, state.currentHoveredLotId)?.classList.remove(LOT_HOVERED_CLASS);
        state.currentHoveredLotId = null;
      }
    },
  };
}

export default createLotPolygonColors;